import { derived, get, writable } from 'svelte/store';
import { isNonProductionAppBuild } from 'src/helpers/dev_tools';
import type { EmeterPriorityDevForce } from 'src/helpers/emeter_priority_layout';
import {
	claimPriorityLayoutDevHud,
	priorityLayoutDevHudKind,
	priorityLayoutDevHudPosition,
	resetPriorityLayoutDevHudPosition,
	setPriorityLayoutDevHudPosition,
	type PriorityLayoutDevHudPosition,
} from 'src/stores/features/dev/priority-layout-dev-hud-state';

const STORAGE_KEY = 'shelly_dev_emeter_priority_force';

const readInitialValue = (): EmeterPriorityDevForce => {
	if (!isNonProductionAppBuild()) return 'auto';
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (stored) return stored as EmeterPriorityDevForce;
	} catch {
		// localStorage may be unavailable
	}
	return 'auto';
};

export type EmeterPriorityDevHudFamily = 'triphase' | 'single';

/**
 * Dev-only Pro 3EM / Pro EM 50 Main-tab overlay / grid force.
 * Production builds stay on auto. Preference is local-only.
 */
export const emeterPriorityDevForce = writable<EmeterPriorityDevForce>(readInitialValue());

/** Which HUD actions apply: triphase includes Imbalance, single-channel does not. */
export const emeterPriorityDevHudFamily = writable<EmeterPriorityDevHudFamily>('triphase');

emeterPriorityDevForce.subscribe((value) => {
	if (!isNonProductionAppBuild()) return;
	try {
		localStorage.setItem(STORAGE_KEY, value);
	} catch {
		// localStorage may be unavailable
	}
});

export type EmeterPriorityDevHudPosition = PriorityLayoutDevHudPosition;

export const emeterPriorityDevHudPosition = priorityLayoutDevHudPosition;
export const setEmeterPriorityDevHudPosition = setPriorityLayoutDevHudPosition;
export const resetEmeterPriorityDevHudPosition = resetPriorityLayoutDevHudPosition;

/** True while the emeter Main tab owns the shared layout HUD. */
export const emeterPriorityDevHudOpen = derived(priorityLayoutDevHudKind, (kind) => kind === 'emeter');

export const setEmeterPriorityDevHudFamily = (family: EmeterPriorityDevHudFamily) => {
	if (!isNonProductionAppBuild()) {
		emeterPriorityDevHudFamily.set('triphase');
		return;
	}
	emeterPriorityDevHudFamily.set(family);
	if (family === 'single' && get(emeterPriorityDevForce) === 'imbalance') {
		emeterPriorityDevForce.set('auto');
	}
};

/** Keep the HUD mounted outside the sidepanel; last claim wins if two views overlap. */
export const claimEmeterPriorityDevHud = (
	family: EmeterPriorityDevHudFamily = 'triphase',
): (() => void) => {
	setEmeterPriorityDevHudFamily(family);
	return claimPriorityLayoutDevHud('emeter');
};

export const getEmeterPriorityDevForce = (): EmeterPriorityDevForce =>
	isNonProductionAppBuild() ? get(emeterPriorityDevForce) : 'auto';

export const setEmeterPriorityDevForce = (force: EmeterPriorityDevForce) => {
	emeterPriorityDevForce.set(isNonProductionAppBuild() ? force : 'auto');
};
